import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SUFFIX = 'RTG Nexus';

/** Admin tab → label. Keep in sync with AdminDashboard's TAB_META titles. */
const ADMIN_TAB_LABELS = {
  overview: 'Overview',
  users: 'User Permissions',
  departments: 'Departments',
  dept_managers: 'Department Managers',
  categories: 'Categories',
  tools: 'Tools',
  apps: 'Apps',
  activity: 'Activity Logs',
  vendors: 'Vendors',
  sync: 'Odoo Sync',
  settings: 'Settings',
};

/**
 * Resolve a page label from a pathname. Admin pages return an `admin: true`
 * flag so the caller can prefix "Admin · ". Pure → unit-testable.
 *
 * @param {string} pathname
 * @returns {{ label: string|null, admin: boolean }}
 */
export function resolvePageTitle(pathname) {
  const path = (pathname || '/').replace(/\/+$/, '') || '/';

  // Admin surface.
  if (path === '/admin') return { label: 'Overview', admin: true };
  if (path.startsWith('/admin/apps/')) return { label: 'App Detail', admin: true };
  if (path.startsWith('/admin/')) {
    const tab = path.slice('/admin/'.length).split('/')[0];
    return { label: ADMIN_TAB_LABELS[tab] || 'Admin', admin: true };
  }

  // User surface.
  if (path === '/') return { label: 'My Tools', admin: false };
  if (path === '/requests') return { label: 'Tool requests', admin: false };
  if (path === '/profile') return { label: 'My Profile', admin: false };
  if (path.startsWith('/profile/')) return { label: 'Profile', admin: false };
  if (path === '/build-app') return { label: 'Build Your App', admin: false };
  if (path.startsWith('/build-app/')) return { label: 'App Detail', admin: false };
  if (path === '/dept' || path.startsWith('/dept/')) return { label: 'Department', admin: false };
  if (path === '/results-chain') return { label: 'Results Chain', admin: false };
  if (path === '/business-ontology') return { label: 'Business Ontology', admin: false };
  // Labels match the nav wording, so the browser tab says what the sidebar item
  // the reader clicked said.
  if (path === '/people') return { label: 'Employee Directory', admin: false };
  if (path === '/org-chart') return { label: 'Org Chart', admin: false };
  if (path === '/vendors') return { label: 'Vendors', admin: false };
  if (path.startsWith('/vendors/')) return { label: 'Vendor', admin: false };

  return { label: null, admin: false };
}

/**
 * Build the full document title. "<label> - RTG Nexus", admin pages get an
 * "Admin · " prefix on the label. Falls back to the bare suffix when unknown.
 */
export function buildDocumentTitle(pathname) {
  const { label, admin } = resolvePageTitle(pathname);
  if (!label) return SUFFIX;
  const prefixed = admin ? `Admin · ${label}` : label;
  return `${prefixed} - ${SUFFIX}`;
}

/**
 * Side-effect hook: keeps document.title in sync with the current route.
 * Mount once inside the Router.
 */
export default function usePageTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = buildDocumentTitle(pathname);
  }, [pathname]);
}
